/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../modules/auth/useAuth";
import {
  Modal, Button, TextField, Toggle,
} from "@xfrontend/xui-react-plus";
import { toast } from "#/infra/Toastify";
import { createUser } from "#/apis/infra/users";
import { LicenseItem } from "#/apis/infra/listLicenses";
import { CustomerItem } from "#/apis/infra/customers";
import RoleScopePicker, { RoleScopeValue } from "./RoleScopePicker";

const StyledModal = styled(Modal)`
  .modal-dialog { width: 520px; max-width: calc(100% - 32px); }
  .modal-content { background: var(--x-bg-500, #2d293a); border: 1px solid var(--x-purple-500, #494358); }
`;

const Field = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 12.5px; color: var(--x-gray-400, #cfd0d3); margin-bottom: 5px; }
`;

const Row = styled.div`
  display: flex; align-items: center; gap: 9px; margin-top: 4px;
  color: var(--x-gray-300, #e9e9eb); font-size: 13px;
`;

type UserFormModalProps = {
  show: boolean;
  orgs: LicenseItem[];
  customers: CustomerItem[];
  onClose: () => void;
  onCreated: () => void;
};

const UserFormModal = ({
  show, orgs, customers, onClose, onCreated,
}: UserFormModalProps): JSX.Element => {
  const { t } = useTranslation("management");
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [roleScope, setRoleScope] = useState<RoleScopeValue>({
    role: "ORG_USER", scope_type: "LICENSE", scope_id: orgs[0]?.license_id ?? "",
  });
  const [busy, setBusy] = useState(false);

  // Reset the form each time the create dialog opens (the modal stays mounted between opens),
  // and pick up the current orgs for the default scope target.
  useEffect(() => {
    if (show) {
      setEmail("");
      setPassword("");
      setSendInvite(true);
      setRoleScope({ role: "ORG_USER", scope_type: "LICENSE", scope_id: orgs[0]?.license_id ?? "" });
    }
  }, [show, orgs]);

  const submit = (): void => {
    const token = auth.user?.access_token;
    if (!token || busy) return;
    setBusy(true);
    createUser({
      token,
      body: {
        username: email,
        email,
        password,
        role: roleScope.role,
        scope_type: roleScope.scope_type,
        scope_id: roleScope.scope_id,
        send_invite: sendInvite,
      },
    }).then((res) => {
      setBusy(false);
      if (res.success) {
        toast.success(t("toast.created"));
        onCreated();
        onClose();
      } else {
        toast.error(res.msg || t("toast.failed"));
      }
    });
  };

  const valid = email.trim() !== "" && password.trim() !== "" && roleScope.scope_id !== "";

  return (
    <StyledModal title={t("form.createTitle")} show={show} onClose={onClose} showCloseBtn restoreFocus={false}>
      <Modal.Body>
        <Field>
          <label htmlFor="uf-email">{t("form.username")}</label>
          <TextField
            id="uf-email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          />
        </Field>
        <Field>
          <label htmlFor="uf-pw">{t("form.password")}</label>
          <TextField
            id="uf-pw"
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          />
        </Field>
        <Field>
          <label htmlFor="uf-role">{`${t("form.role")} / ${t("form.scopeTarget")}`}</label>
          <RoleScopePicker orgs={orgs} customers={customers} value={roleScope} onChange={setRoleScope} />
        </Field>
        <Row>
          <Toggle name="send-invite" checked={sendInvite} onChange={() => setSendInvite((v) => !v)} />
          {t("form.sendInvite")}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t("form.cancel")}</Button>
        <Button variant="primary" disabled={!valid || busy} onClick={submit}>{t("form.submit")}</Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default UserFormModal;
